/**
 * REALISM HOUSE RULES — D-EZ-83 … D-EZ-87.
 *
 * Howard, 4 Oct 2026: all five realism rules approved — "track personality,
 * superspeedway pack racing, losing laps to pace, clean air on restarts, an era
 * dial. Default ON (modern era), each switchable, old behaviour reachable by
 * turning off."
 *
 * THESE ARE HOUSE RULES, NOT PRINTED ONES, and every event they produce says so
 * in its citation (`house rule D-EZ-8x`). They exist because the printed game,
 * measured over 360 races, ran far from a modern Cup race in five places:
 * cautions (4.4/race, short tracks among the LOWEST), lead changes (6.3/race,
 * superspeedways 6.8), DNFs (7.5/race, 3.6 of them mechanical), cars a lap down
 * at the flag (1.9) and the value of track position (pole won 7%).
 *
 * THE OFF SWITCH IS EXACT. Each rule is a `RaceOptions` field that defaults ON;
 * set it off and the race is byte-for-byte the race the engine ran before the
 * rule existed — no die is rolled, nothing moves. `HOUSE_RULES_OFF` turns all
 * five off at once and is what a test (or a league that wants the printed game)
 * passes. `houseRules.test.ts` pins off == old on fixed seeds.
 *
 * Where a source mechanism existed it was used rather than invented:
 *   D-EZ-84 uses EZRA-S2 §3.10's own Draft Event, extended to every car — and
 *           to the lead pack (TOP group) on every superspeedway quality turn,
 *           whichever group the race cam called (only the trigger is widened);
 *   D-EZ-85 feeds EZRA's existing laps-down sub-group and Lucky Dog;
 *   D-EZ-86 uses the printed brake-symbol bonus ladder (p.vii) and its ceiling;
 *   D-EZ-87 reuses the 2025 set's green thumbs-up triangle idea (D-EZ-76) —
 *           a failed PROBLEM repair costs laps, not the race.
 * D-EZ-83 has no printed source for a per-track RATE (the Trouble Matrix's
 * Chart A/B/C and the Laurence Davis option sheet vary the KIND and SEVERITY of
 * trouble by track, never how often it happens), so its weights are house
 * numbers, calibrated by measurement and documented in `TRACK_TEMPER`.
 */

import type { Dice } from './dice.ts';
import type { DiceRecord, Modifier, PositionDelta, RuleEvent } from './ruleEvent.ts';
import { resolveRepair, type TrackAdjustment } from './repair.ts';
import { rollDriverFinder, type Era } from './problem.ts';
import { adjustedEscape, type TroubleField, type TroubleGroup } from './trouble.ts';
import { groupOf } from './types.ts';

/** D-EZ-87 — which era's machinery the field is running (declared beside its rule, in problem.ts). */
export type { Era };

/** The five switches, as `RaceOptions` carries them. Absent = ON / 'modern'. */
export interface HouseRuleOptions {
  /** D-EZ-83 — track personality: a per-track "track temper" die. */
  trackPersonality?: false;
  /** D-EZ-84 — superspeedway pack racing: every car can draft at a superspeedway. */
  packRacing?: false;
  /** D-EZ-85 — laps lost to pace on long green runs. */
  lapsToPace?: false;
  /** D-EZ-86 — clean air for the front on the start and every restart. */
  cleanAir?: false;
  /** D-EZ-87 — the era dial. Default 'modern'; '1990s' is the printed game. */
  era?: Era;
}

/** Every house rule switched off: the printed game, exactly as it ran before. */
export const HOUSE_RULES_OFF: Readonly<Required<HouseRuleOptions>> = Object.freeze({
  trackPersonality: false,
  packRacing: false,
  lapsToPace: false,
  cleanAir: false,
  era: '1990s',
});

export interface HouseRuleInfo {
  key: keyof HouseRuleOptions;
  decision: string;
  /** Player-facing name. */
  label: string;
  /** One plain sentence for a settings screen. */
  summary: string;
  defaultOn: true;
}

/** For a settings screen: the five rules, in the order Howard approved them. */
export const HOUSE_RULES: readonly HouseRuleInfo[] = [
  {
    key: 'trackPersonality',
    decision: 'D-EZ-83',
    label: 'House rule: track personality',
    summary: 'Every kind of track has its own temper — short tracks bring out the most cautions, road courses the fewest.',
    defaultOn: true,
  },
  {
    key: 'packRacing',
    decision: 'D-EZ-84',
    label: 'House rule: superspeedway pack racing',
    summary: 'At Daytona and Talladega every car can work the draft, and the lead pack is drafting every lap, so the lead swaps hands all day.',
    defaultOn: true,
  },
  {
    key: 'lapsToPace',
    decision: 'D-EZ-85',
    label: 'House rule: laps lost to pace',
    summary: 'On a long green run the slowest cars at the back get lapped by the leader.',
    defaultOn: true,
  },
  {
    key: 'cleanAir',
    decision: 'D-EZ-86',
    label: 'House rule: clean air on restarts',
    summary: 'The leader and the front of the field are harder to pass on the start and every restart.',
    defaultOn: true,
  },
  {
    key: 'era',
    decision: 'D-EZ-87',
    label: 'House rule: modern era',
    summary: 'Modern cars rarely break: a car with a mechanical problem usually goes to the garage and comes back laps down instead of retiring.',
    defaultOn: true,
  },
];

export const CITE_DAVIS_CRASH =
  'RWBR_trouble_option.pdf (Laurence Davis), CRASHES — "Drivers who passed ESCAPE ... have lost position on the ' +
  'track but DO NOT need REPAIR dice roll! Instead, these drivers roll two dice ... to determine how many positions ' +
  'they have lost."';

export const CITE_HOUSE = {
  trackPersonality: 'House rule D-EZ-83 — track personality (track temper die)',
  packRacing: 'House rule D-EZ-84 — superspeedway pack racing (EZRA-S2 §3.10 Draft Event for every car)',
  lapsToPace: 'House rule D-EZ-85 — laps lost to pace on long green runs',
  cleanAir: 'House rule D-EZ-86 — clean air on the start and restarts (p.vii brake bonus ladder)',
  era: 'House rule D-EZ-87 — era dial, modern: a failed PROBLEM repair goes to the garage, laps down',
} as const;

/** Resolved, so the engine never reads an `undefined` as anything but "on". */
export interface HouseRulesInForce {
  trackPersonality: boolean;
  packRacing: boolean;
  lapsToPace: boolean;
  cleanAir: boolean;
  era: Era;
}

export function houseRulesInForce(options: HouseRuleOptions = {}): HouseRulesInForce {
  return {
    trackPersonality: options.trackPersonality !== false,
    packRacing: options.packRacing !== false,
    lapsToPace: options.lapsToPace !== false,
    cleanAir: options.cleanAir !== false,
    era: options.era ?? 'modern',
  };
}

/** All five off, resolved — what `runTurn` assumes when it is handed nothing. */
export const PRINTED_GAME: Readonly<HouseRulesInForce> = Object.freeze(houseRulesInForce(HOUSE_RULES_OFF));

// ---------------------------------------------------------------------------
// D-EZ-83 — TRACK PERSONALITY: the track temper die
// ---------------------------------------------------------------------------

type Category = 'Short Track' | 'Super Speedway' | 'Intermediate Track' | 'Road Course';

/**
 * THE TRACK TEMPER. On a green-flag turn whose RACE CAM called a QUALITY (so
 * the turn had no incident of its own) and on which no caution has already
 * come out, roll one d10 after the action phase. At or under the track's
 * number, a car gets loose: the Driver Finder of the called group names him,
 * he SPINS (a performance chip saves him, p.xiv) and the yellow comes out —
 * one car is one caution turn (p.v). What the spin costs him is Laurence
 * Davis's printed CRASH procedure (RWBR_trouble_option.pdf): an ESCAPE roll;
 * made, he "lost position on the track" — two dice, added, places back (never
 * off the lead lap); missed, the ordinary TROUBLE repair roll decides whether
 * he limps on (a lap down with that rule on) or is out.
 *
 * HOUSE NUMBERS, calibrated against 360 measured races so that cautions land
 * at 6–8 a race with short tracks hottest, then superspeedways, intermediates
 * and road courses (see the commit message for the table). Unknown categories
 * get no temper die at all.
 */
export const TRACK_TEMPER: Readonly<Record<Category, number>> = {
  'Short Track': 5,
  'Super Speedway': 1,
  'Intermediate Track': 1,
  'Road Course': 2,
};

export function trackTemperFor(category: string): number {
  return (TRACK_TEMPER as Record<string, number>)[category] ?? 0;
}

export interface LooseCarResult extends RuleEvent {
  eventType: 'track-temper';
  momentTag: 'temper.calm' | 'temper.spin' | 'temper.loose-car' | 'temper.chip-saved' | 'temper.no-driver';
  die: number;
  /** Fields `turn.ts`'s `absorb` reads. */
  yellowFlag: boolean;
  yellowTurns: 0 | 1;
  collectedIds: string[];
  chipSavedIds: string[];
  driverId: string | null;
}

/**
 * Roll the temper die and, if it bites, resolve the loose car. Pure apart from
 * the dice; the caller folds the result into the field like any incident.
 */
export function resolveTrackTemper(input: {
  field: TroubleField;
  group: TroubleGroup;
  category: string;
  turn: number;
  totalTurns: number;
  escapeAdj?: TrackAdjustment;
  repairAdj?: TrackAdjustment;
  lapsDown: boolean;
  /** The last position still on the lead lap, so a spin never drops a car a lap. */
  lastLeadLapPosition: number;
  dice: Dice;
}): LooseCarResult {
  const { dice } = input;
  const band = trackTemperFor(input.category);
  const die = dice.d10();
  const dieRecord: DiceRecord = { kind: 'd10', dice: [die], label: `track temper (1-${band} = a car gets loose)` };
  const modifiers: Modifier[] = [
    { name: 'track temper', value: die, detail: `${input.category}: 1-${band} on a d10` },
  ];
  const quiet = (momentTag: LooseCarResult['momentTag'], description: string, extra: DiceRecord[] = []): LooseCarResult => ({
    eventType: 'track-temper',
    momentTag,
    ruleCitation: CITE_HOUSE.trackPersonality,
    actorIds: [],
    positionDeltas: [],
    modifiers,
    dice: [dieRecord, ...extra],
    description,
    die,
    yellowFlag: false,
    yellowTurns: 0,
    collectedIds: [],
    chipSavedIds: [],
    driverId: null,
  });
  if (die > band) return quiet('temper.calm', 'The track behaves itself.');

  const finder = rollDriverFinder(input.group, input.field, dice);
  const car = finder.car;
  if (!car) return quiet('temper.no-driver', 'Somebody gets loose — but there is nobody running in that part of the field.', [finder.dice]);
  const who = car.name ?? car.driverId;

  // p.xiv — "A driver can use a performance chip to avoid trouble." Bottom-group
  // cars cannot carry one (p.xv).
  if ((car.chips ?? 0) > 0 && groupOf(car.position) !== 'bottom') {
    return {
      ...quiet('temper.chip-saved', `${who} gets loose and saves it — it costs him his performance chip, but he keeps the car pointed the right way.`, [finder.dice]),
      actorIds: [car.driverId],
      chipSavedIds: [car.driverId],
      driverId: car.driverId,
    };
  }

  // He spun. Laurence Davis's CRASH reading (RWBR_trouble_option.pdf) decides
  // what the spin cost him: "Drivers who passed ESCAPE ... have lost position
  // on the track but DO NOT need REPAIR dice roll! Instead, these drivers roll
  // two dice (adding the total) to determine how many positions they have
  // lost." Fail the ESCAPE and it is the ordinary repair roll (RB/v).
  const escape = adjustedEscape(car.escape, input.escapeAdj);
  const escapeRoll = dice.sum2d6();
  const escapeRecord: DiceRecord = {
    kind: '2d6-sum',
    dice: escapeRoll.dice,
    total: escapeRoll.total,
    label: `ESCAPE check (${escape}) — does he catch it?`,
  };
  const spun = {
    eventType: 'track-temper' as const,
    ruleCitation: `${CITE_HOUSE.trackPersonality}; ${CITE_DAVIS_CRASH}`,
    actorIds: [car.driverId],
    die,
    yellowFlag: true,
    yellowTurns: 1 as const,
    collectedIds: [car.driverId],
    chipSavedIds: [],
    driverId: car.driverId,
  };

  if (escapeRoll.total <= escape) {
    const lost = dice.sum2d6();
    const to = Math.max(car.position, Math.min(car.position + lost.total, input.lastLeadLapPosition));
    return {
      ...spun,
      momentTag: 'temper.spin',
      positionDeltas: to === car.position ? [] : [{ driverId: car.driverId, from: car.position, to, note: 'house rule D-EZ-83 — spun, lost track position' }],
      modifiers: [...modifiers, ...finder.modifiers, { name: 'ESCAPE', value: `${escapeRoll.total} vs ${escape}`, detail: 'caught it — no damage' }, { name: 'positions lost', value: lost.total }],
      dice: [dieRecord, finder.dice, escapeRecord, { kind: '2d6-sum', dice: lost.dice, total: lost.total, label: 'positions lost (CRASH, second-try escape)' }],
      description: `${who} gets loose and spins — the yellow is out. No damage, but by the time he is pointed the right way he has lost ${to - car.position} place${to - car.position === 1 ? '' : 's'}.`,
    };
  }

  const repair = resolveRepair({
    car: { driverId: car.driverId, name: car.name, pitCrewGrade: car.pitCrewGrade, position: car.position, chips: car.chips ?? 0 },
    table: 'trouble',
    turn: input.turn,
    totalTurns: input.totalTurns,
    repairAdj: input.repairAdj,
    dice,
    cause: 'a car got loose',
    lapsDown: input.lapsDown,
  });
  const delta: PositionDelta = {
    driverId: car.driverId,
    from: car.position,
    to: null,
    placement: repair.repaired ? (input.lapsDown ? 'laps-down' : 'end-of-bottom') : 'out-of-race',
    note: 'house rule D-EZ-83 — a car got loose',
  };
  return {
    ...spun,
    momentTag: 'temper.loose-car',
    positionDeltas: [delta],
    modifiers: [...modifiers, ...finder.modifiers, { name: 'ESCAPE', value: `${escapeRoll.total} vs ${escape}`, detail: 'into the wall' }, ...repair.modifiers],
    dice: [dieRecord, finder.dice, escapeRecord, ...repair.dice],
    description: repair.repaired
      ? `${who} gets loose and backs it into the wall — the yellow is out. The crew patches it up, but it costs him ${input.lapsDown ? 'a lap' : 'his track position'}.`
      : `${who} gets loose and hits the wall hard — the yellow is out, and that car is done for the day.`,
  };
}

// ---------------------------------------------------------------------------
// D-EZ-85 — LAPS LOST TO PACE
// ---------------------------------------------------------------------------

/**
 * After this many consecutive green-flag turns (pit turns under green count —
 * the field is still racing), the leader starts putting the slowest cars a lap
 * down: one car at the end of every green turn from then on, until a caution
 * resets the run. Road courses are long laps with spread-out traffic, so it
 * takes longer to lap anyone there. House numbers, calibrated by measurement.
 */
export const PACE_LAP_AFTER: Readonly<Record<Category, number>> = {
  'Short Track': 1,
  'Super Speedway': 4,
  'Intermediate Track': 4,
  'Road Course': 5,
};

export function paceLapAfter(category: string): number {
  return (PACE_LAP_AFTER as Record<string, number>)[category] ?? 3;
}

/** How many of the last lead-lap cars are looked at for the slowest. */
export const PACE_WINDOW = 3;

// ---------------------------------------------------------------------------
// D-EZ-86 — CLEAN AIR
// ---------------------------------------------------------------------------

/** Extra brake symbols for a LEADER/TOP defender on a start or restart turn. */
export const CLEAN_AIR_BRAKE_BONUS = 1;
